import {createStyles} from '@mantine/core';
import React, {useEffect, useRef, useState} from 'react';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import type {
  PredictionJobPropertiesQuery,
  PredictionJobPropertiesQueryVariables,
} from 'model-hub/__generated/PredictionJobPropertiesQuery';
import {PREDICTION_JOB_PROPERTIES_QUERY} from 'model-hub/ModelHubQueries';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {PropertyResultsTable, type PropertyDefinition} from './PropertyResultsTable';

interface PropertyResultsPreviewProps {
  jobId: string;
  opened: boolean;
  propertyDefinitions: ReadonlyArray<PropertyDefinition>;
  pageSize?: number;
}

export default function PropertyResultsPreview({
  jobId,
  opened,
  propertyDefinitions,
  pageSize,
}: PropertyResultsPreviewProps): JSX.Element | null {
  const {classes} = useStyles();
  const [properties, setProperties] = useState<Record<string, unknown> | null | undefined>(undefined);
  const requestState = useRef<'idle' | 'loading' | 'loaded'>('idle');

  useEffect(() => {
    if (!opened || requestState.current !== 'idle') {
      return;
    }

    let active = true;
    requestState.current = 'loading';
    doQuerySafe<PredictionJobPropertiesQuery, PredictionJobPropertiesQueryVariables>(
      {query: PREDICTION_JOB_PROPERTIES_QUERY},
      {variables: {id: jobId}, fetchPolicy: 'no-cache'}
    )
      .then((result) => {
        if (active) {
          setProperties(result.predictionJob?.properties ?? null);
          requestState.current = 'loaded';
        }
      })
      .catch(() => {
        if (active) {
          setProperties(null);
          requestState.current = 'loaded';
        }
      });

    return () => {
      active = false;
      if (requestState.current === 'loading') {
        requestState.current = 'idle';
      }
    };
  }, [jobId, opened]);

  if (!opened) {
    return null;
  }

  return (
    <div className={classes.section}>
      {properties === undefined ? (
        <CenteredHorizontalThrobber />
      ) : (
        <PropertyResultsTable
          pageSize={pageSize}
          properties={properties}
          propertyDefinitions={propertyDefinitions}
        />
      )}
    </div>
  );
}

const useStyles = createStyles(() => ({
  section: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));
